class SeekerReviewsSlider extends HTMLElement {
  #current = 0;
  #interval;
  #mediaQuery = window.matchMedia('(min-width: 750px)');
  #resizeObserver;
  #pointerStartX = 0;
  #pointerDeltaX = 0;
  #isDragging = false;

  connectedCallback() {
    this.viewport = this.querySelector('.seeker-reviews__viewport');
    this.track = this.querySelector('.seeker-reviews__track');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];

    if (this.slides.length <= 1) {
      this.slides.forEach((slide) => slide.classList.add('is-active'));
      return;
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.querySelector('[data-action="previous"]')?.addEventListener('click', () => {
      this.goTo(this.#current - 1);
    });

    this.querySelector('[data-action="next"]')?.addEventListener('click', () => {
      this.next();
    });

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());

    this.#mediaQuery.addEventListener('change', () => {
      this.#syncMode();
      this.goTo(this.#clampIndex(this.#current));
    });

    if (this.viewport && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (this.#isDesktopMode()) {
          this.#updateDesktopPosition();
        } else {
          this.#resetMobileLayout();
        }
      });
      this.#resizeObserver.observe(this.viewport);
    }

    window.addEventListener('resize', this.#handleResize);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#setupDrag();
    this.#syncMode();
    this.goTo(0);
    this.play();
  }

  disconnectedCallback() {
    this.pause();
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#handleResize);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#teardownDrag();
  }

  #isDesktopMode() {
    return this.#mediaQuery.matches;
  }

  #syncMode() {
    this.classList.toggle('is-desktop', this.#isDesktopMode());

    if (this.#isDesktopMode()) {
      this.#updateDesktopPosition();
      return;
    }

    this.#resetMobileLayout();
  }

  #resetMobileLayout() {
    if (this.track) {
      this.track.style.transform = '';
    }

    this.viewport?.style.removeProperty('--seeker-reviews-slide-size');
    this.slides.forEach((slide) => {
      slide.style.removeProperty('flex-basis');
      slide.style.removeProperty('width');
    });
  }

  #maxIndex() {
    if (this.#isDesktopMode()) {
      return Math.max(0, this.slides.length - 2);
    }

    return this.slides.length - 1;
  }

  #clampIndex(index) {
    return Math.max(0, Math.min(index, this.#maxIndex()));
  }

  goTo(index) {
    this.#current = this.#clampIndex(index);

    if (this.#isDesktopMode()) {
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.#current;
        const isVisible = slideIndex >= this.#current && slideIndex <= this.#current + 1;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
      this.#updateDesktopPosition();
    } else {
      this.#resetMobileLayout();

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.#current;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', `${!isActive}`);
      });
    }

    this.dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === this.#current;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', `${isActive}`);
    });
  }

  next() {
    if (this.#current >= this.#maxIndex()) {
      this.goTo(0);
      return;
    }

    this.goTo(this.#current + 1);
  }

  #updateDesktopPosition() {
    if (!this.#isDesktopMode() || !this.track || !this.slides[0] || !this.viewport) return;

    requestAnimationFrame(() => {
      const viewportWidth = this.viewport.offsetWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const visibleCount = 2;
      const slideWidth =
        viewportWidth > 0
          ? (viewportWidth - gap * (visibleCount - 1)) / visibleCount
          : this.slides[0].offsetWidth;

      if (!slideWidth) return;

      this.viewport.style.setProperty('--seeker-reviews-slide-size', `${slideWidth}px`);
      this.slides.forEach((slide) => {
        slide.style.flexBasis = `${slideWidth}px`;
        slide.style.width = `${slideWidth}px`;
      });

      const offset = this.#current * (slideWidth + gap);

      this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
    });
  }

  #handleResize = () => {
    if (this.#isDesktopMode()) {
      this.#updateDesktopPosition();
    } else {
      this.#resetMobileLayout();
    }
  };

  #setupDrag() {
    this.#onPointerDown = this.#onPointerDown.bind(this);
    this.#onPointerMove = this.#onPointerMove.bind(this);
    this.#onPointerUp = this.#onPointerUp.bind(this);

    this.viewport?.addEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.addEventListener('pointermove', this.#onPointerMove);
    this.viewport?.addEventListener('pointerup', this.#onPointerUp);
    this.viewport?.addEventListener('pointercancel', this.#onPointerUp);
  }

  #teardownDrag() {
    this.viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.removeEventListener('pointermove', this.#onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.#onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.#onPointerUp);
  }

  #onPointerDown(event) {
    if (!this.#isDesktopMode()) return;

    this.#isDragging = true;
    this.#pointerStartX = event.clientX;
    this.#pointerDeltaX = 0;
    this.pause();
    this.viewport.setPointerCapture(event.pointerId);
  }

  #onPointerMove(event) {
    if (!this.#isDragging) return;

    this.#pointerDeltaX = event.clientX - this.#pointerStartX;
  }

  #onPointerUp(event) {
    if (!this.#isDragging) return;

    this.#isDragging = false;

    if (this.viewport.hasPointerCapture(event.pointerId)) {
      this.viewport.releasePointerCapture(event.pointerId);
    }

    const threshold = 48;

    if (this.#pointerDeltaX <= -threshold) {
      this.next();
    } else if (this.#pointerDeltaX >= threshold) {
      this.goTo(this.#current - 1);
    } else {
      this.#updateDesktopPosition();
    }

    this.play();
  }

  play() {
    this.pause();

    if (this.getAttribute('data-autoplay-enabled') === 'false') return;

    const seconds = parseInt(this.getAttribute('data-scroll-timer') || '0', 10);
    if (!seconds || this.slides.length <= 1) return;

    this.#interval = window.setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, seconds * 1000);
  }

  pause() {
    if (this.#interval) {
      window.clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.play();
    }
  };
}

if (!customElements.get('seeker-reviews-slider')) {
  customElements.define('seeker-reviews-slider', SeekerReviewsSlider);
}
